import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Form } from '@edgebox/react-components';
import { InternalId } from '@edgebox/data-definition-kit';
import { AiModelStatus } from '@edgebox/sync-core-data-definitions';
import type { ISyncCoreApi } from '@edgebox/sync-core-rest-client';
import { ClientAiModelEntity } from '@edgebox/sync-core-rest-client';

export function AiModelSelector({
  api,
  value,
  onChange,
  disabled,
  name,
}: {
  api: ISyncCoreApi;
  value: InternalId | undefined;
  onChange: (nextId: InternalId | undefined, nextEntity: ClientAiModelEntity | undefined) => void;
  disabled?: boolean;
  name?: string;
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<unknown>(undefined);
  const [models, setModels] = useState<ClientAiModelEntity[]>([]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(undefined);

    api.ai.models
      .list(
        { page: 0, itemsPerPage: 200 },
        {
          status: [AiModelStatus.Active, AiModelStatus.Draft],
        }
      )
      .then((res) => {
        if (cancelled) return;
        setModels(res.items ?? []);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e);
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [api]);

  const options = useMemo(() => {
    return models
      .slice()
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
      .map((m) => ({ id: m.id, name: m.name, status: m.status, entity: m }));
  }, [models]);

  const selectedId = value ?? '';

  if (error) {
    return (
      <Alert variant="danger" className="mb-0">
        Failed to load models: {error instanceof Error ? error.message : String(error)}
      </Alert>
    );
  }

  return (
    <Form.Select
      value={selectedId}
      disabled={disabled || loading}
      name={name}
      id={name}
      onChange={(e) => {
        const id = e.target.value;
        if (!id) {
          onChange(undefined, undefined);
          return;
        }

        const model = options.find((o) => o.id === id)?.entity;
        if (!model) {
          onChange(undefined, undefined);
          return;
        }

        onChange(model.id, model);
      }}
    >
      <option value="">Select a model…</option>
      {options.map((m) => (
        <option key={m.id} value={m.id}>
          {m.name} ({m.status})
        </option>
      ))}
    </Form.Select>
  );
}
